import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { alpha, Box, Button, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useToastService } from '@@common';

import { codeFontFamily, getCodeSyntaxTheme } from '../themes/codeTokens';
import CopyToClipboardButton from './Clipboard/CopyToClipboardButton';
import PasteFromClipboardButton from './Clipboard/PasteFromClipboardButton';

type CodeFileViewerProps = {
	path: string;
	content?: string | null;
	language?: string;
	loading?: boolean;
	showHeader?: boolean;
	editable?: boolean;
	saving?: boolean;
	onSave?: (content: string) => Promise<void> | void;
	onDirtyChange?: (dirty: boolean) => void;
};

export function CodeFileViewer({
	path,
	content,
	language = languageForPath(path),
	loading = false,
	showHeader = true,
	editable = false,
	saving = false,
	onSave,
	onDirtyChange,
}: CodeFileViewerProps) {
	const theme = useTheme();
	const toastService = useToastService();
	const syntaxTheme = getCodeSyntaxTheme(theme);
	const loadedContent = content ?? '';
	const [draftContent, setDraftContent] = useState(loadedContent);
	const [savedContent, setSavedContent] = useState(loadedContent);
	const [isEditing, setIsEditing] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const [matchIndex, setMatchIndex] = useState(0);
	const codeContainerRef = useRef<HTMLDivElement>(null);
	const matchingLines = useMemo(() => {
		if (!searchText || loading) return [];
		const query = searchText.toLowerCase();
		return loadedContent.split('\n').flatMap((line, index) => line.toLowerCase().includes(query) ? [index + 1] : []);
	}, [loadedContent, loading, searchText]);
	const matchingLineSet = useMemo(() => new Set(matchingLines), [matchingLines]);
	const activeLine = matchingLines[Math.min(matchIndex, matchingLines.length - 1)];

	useEffect(() => {
		setMatchIndex(0);
	}, [searchText, loadedContent, path]);

	useEffect(() => {
		const container = codeContainerRef.current;
		const line = container?.querySelector<HTMLElement>(`[data-code-line="${activeLine}"]`);
		if (container && line && !isEditing) {
			container.scrollTop += line.getBoundingClientRect().top - container.getBoundingClientRect().top - container.clientHeight / 3;
		}
	}, [activeLine, isEditing, loadedContent, path]);

	const moveSearchMatch = (direction: number) => {
		if (matchingLines.length) {
			setMatchIndex((index) => (index + direction + matchingLines.length) % matchingLines.length);
		}
	};
	const closeSearch = () => {
		setIsSearchOpen(false);
		setSearchText('');
	};

	useEffect(() => {
		setDraftContent(loadedContent);
		setSavedContent(loadedContent);
		setIsEditing(false);
	}, [loadedContent, path]);

	const hasChanges = useMemo(() => draftContent !== savedContent, [draftContent, savedContent]);
	useEffect(() => { onDirtyChange?.(hasChanges); }, [hasChanges, onDirtyChange]);
	useEffect(() => () => { onDirtyChange?.(false); }, [onDirtyChange]);
	const handleSave = async () => {
		if (!onSave || !hasChanges || saving) return;
		try {
			await onSave(draftContent);
			setSavedContent(draftContent);
			setIsEditing(false);
		} catch {
			// The caller reports the save error; retain the dirty state for retry.
		}
	};
	const handlePaste = async () => {
		try {
			setDraftContent(await navigator.clipboard.readText());
		} catch {
			toastService.error('Unable to read from the clipboard');
		}
	};
	const handleCancelEdit = () => {
		setDraftContent(savedContent);
		setIsEditing(false);
	};

	return (
		<Stack direction="column" flex={1} sx={{ minHeight: 0, overflow: 'hidden' }}>
			{showHeader && (
				<Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
					<Typography variant="body2" noWrap sx={{ color: 'success.main', fontFamily: codeFontFamily }}>
						{path}
					</Typography>
					<Box sx={{ flex: 1 }} />
					{!isEditing && (
						<IconButton
							size="small"
							aria-label="Find text in source"
							title="Find text in source"
							color={isSearchOpen ? 'primary' : 'default'}
							disabled={loading}
							onClick={() => isSearchOpen ? closeSearch() : setIsSearchOpen(true)}
						>
							<SearchIcon fontSize="small" />
						</IconButton>
					)}
					{!isEditing && <CopyToClipboardButton value={loadedContent} title="Copy source to clipboard" edge={false} disabled={loading} />}
					{editable && !isEditing && (
						<Button variant="text" size="small" startIcon={<EditIcon />} disabled={loading} onClick={() => setIsEditing(true)}>
							Edit
						</Button>
					)}
					{editable && isEditing && (
						<>
							{hasChanges && (
								<Typography variant="caption" color="warning.main">
									Unsaved changes
								</Typography>
							)}
							<CopyToClipboardButton value={draftContent} title="Copy source to clipboard" edge={false} disabled={loading} />
							<PasteFromClipboardButton
								onClick={() => void handlePaste()}
								title="Replace source from clipboard"
								edge={false}
								disabled={loading || saving}
							/>
							<Button
								variant="contained"
								size="small"
								startIcon={<SaveIcon />}
								disabled={loading || saving || !hasChanges}
								onClick={() => void handleSave()}
							>
								Save
							</Button>
							<Tooltip title="Cancel editing">
								<span>
									<IconButton size="small" disabled={saving} onClick={handleCancelEdit} aria-label="Cancel editing">
										<CloseIcon fontSize="small" />
									</IconButton>
								</span>
							</Tooltip>
						</>
					)}
				</Stack>
			)}
			{!isEditing && isSearchOpen && (
				<Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
					<TextField
						size="small"
						autoFocus
						placeholder="Find text in source…"
						value={searchText}
						disabled={loading}
						onChange={(event) => setSearchText(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								event.preventDefault();
								moveSearchMatch(event.shiftKey ? -1 : 1);
							} else if (event.key === 'Escape') {
								closeSearch();
							}
						}}
						slotProps={{
							htmlInput: { 'aria-label': 'Find text in source' },
							input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> },
						}}
						sx={{ flex: 1, maxWidth: 360, minWidth: 0 }}
					/>
					{searchText && <Typography variant="caption" color="text.secondary" aria-live="polite" sx={{ whiteSpace: 'nowrap' }}>
						{matchingLines.length ? `${Math.min(matchIndex + 1, matchingLines.length)} / ${matchingLines.length} lines` : 'No matches'}
					</Typography>}
					<IconButton size="small" aria-label="Previous matching line" title="Previous matching line (Shift+Enter)" disabled={!matchingLines.length} onClick={() => moveSearchMatch(-1)}>
						<ArrowUpwardIcon fontSize="small" />
					</IconButton>
					<IconButton size="small" aria-label="Next matching line" title="Next matching line (Enter)" disabled={!matchingLines.length} onClick={() => moveSearchMatch(1)}>
						<ArrowDownwardIcon fontSize="small" />
					</IconButton>
					<Box sx={{ flex: 1 }} />
					<IconButton size="small" aria-label="Close source search" title="Close search (Escape)" onClick={closeSearch}><CloseIcon fontSize="small" /></IconButton>
				</Stack>
			)}
			<Box ref={codeContainerRef} sx={{ bgcolor: 'background.default', overflow: 'auto', flex: 1, minHeight: 0, minWidth: 0, position: 'relative' }}>
				{editable && isEditing ? (
					<Box
						component="textarea"
						aria-label={`${path} source`}
						spellCheck={false}
						value={loading ? '// Loading source…' : draftContent}
						disabled={loading || saving}
						onChange={(event) => setDraftContent(event.target.value)}
						onKeyDown={(event) => {
							if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
								event.preventDefault();
								void handleSave();
							}
						}}
						sx={{
							boxSizing: 'border-box',
							width: '100%',
							height: '100%',
							minHeight: '100%',
							resize: 'none',
							border: 0,
							outline: 0,
							p: '12px 16px',
							bgcolor: 'transparent',
							color: 'text.primary',
							fontFamily: codeFontFamily,
							fontSize: '0.875rem',
							lineHeight: 1.4,
							tabSize: 2,
						}}
					/>
				) : (
					<SyntaxHighlighter
						language={language}
						style={syntaxTheme}
						showLineNumbers
						wrapLines
						lineProps={(lineNumber) => ({
							'data-code-line': lineNumber,
							style: {
								display: 'block',
								backgroundColor: lineNumber === activeLine
									? alpha(theme.palette.primary.main, 0.28)
									: matchingLineSet.has(lineNumber) ? alpha(theme.palette.primary.main, 0.12) : undefined,
							},
						})}
						wrapLongLines={false}
						customStyle={{
							padding: '12px 16px',
							fontFamily: codeFontFamily,
							fontSize: '0.875rem',
							lineHeight: '1.4',
							background: 'transparent',
							boxShadow: 'none',
							margin: 0,
							border: 'none',
							overflow: 'visible',
						}}
					>
						{loading ? '// Loading source…' : content || '// No content available.'}
					</SyntaxHighlighter>
				)}
			</Box>
		</Stack>
	);
}

export function languageForPath(path: string) {
	const extension = path.split('.').pop()?.toLowerCase();
	switch (extension) {
		case 'cs':
			return 'csharp';
		case 'css':
			return 'css';
		case 'ts':
			return 'typescript';
		case 'tsx':
			return 'tsx';
		default:
			return 'text';
	}
}
